/**
 * Pulsewright project file schema (.pwt).
 * Mirrors the schema in PROJECT_BRIEF.md → "Data Schemas".
 */

import type { PatternChannel } from '../constants.js';

export type ExpansionChip = 'VRC6';

export interface Project {
  version: 1;
  metadata: ProjectMetadata;
  instruments: Instrument[];
  samples: Sample[];
  patterns: Pattern[];
  /** Array of pattern indices, played in order. */
  order: number[];
  /** Index into `order` to loop back to, or null for no loop. */
  loop_point: number | null;
  /** Empty array for stock 2A03. */
  expansion_chips: ExpansionChip[];
}

export interface ProjectMetadata {
  title: string;
  author: string;
  /** ISO 8601 timestamp. */
  created_at: string;
  /** BPM-equivalent. */
  tempo: number;
  /** Ticks per row (FamiTracker speed model). */
  speed: number;
}

export type InstrumentType = 'pulse' | 'triangle' | 'noise' | 'dpcm';

export interface Instrument {
  id: number;
  name: string;
  type: InstrumentType;
  envelopes: InstrumentEnvelopes;
  /** Only for DPCM instruments. */
  dpcm_sample_id?: number;
  /** Only for DPCM instruments. e.g. "C-4". */
  dpcm_base_note?: string;
}

export interface InstrumentEnvelopes {
  volume?: Envelope;
  arpeggio?: Envelope;
  pitch?: Envelope;
  /** Only meaningful for pulse instruments. Values: 0/1/2/3 → 12.5/25/50/75%. */
  duty?: Envelope;
}

export interface Envelope {
  values: number[];
  /** Index in `values` to loop to, or null. */
  loop_point: number | null;
  /** Index in `values` representing release point, or null. */
  release_point: number | null;
}

export interface Sample {
  id: number;
  name: string;
  /** Base64-encoded PCM data. */
  data: string;
  sample_rate: number;
}

export interface Pattern {
  id: number;
  /** Number of rows (16–256). Must equal rows.length. */
  length: number;
  rows: PatternRow[];
}

export type PatternRow = Record<PatternChannel, Cell>;

export interface Cell {
  /** Note name like "C-5", "C#5", "OFF", or null. */
  note: string | null;
  instrument: number | null;
  /** Volume 0–15 (NES 4-bit). Displayed in hex. null = no change. */
  volume: number | null;
  effects: Effect[];
}

export interface Effect {
  /** Effect type code, e.g. "0", "1", "A". */
  type: string;
  /** Effect parameter byte (0–255). Displayed as 2-digit hex. */
  param: number;
}

/** Helper: an empty cell (no note, no instrument, no volume, no effects). */
export const EMPTY_CELL: Cell = {
  note: null,
  instrument: null,
  volume: null,
  effects: [],
};
